import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { IndexingNotices } from "@/notices";
import { Onboarding } from "@/onboarding";
import { AcceptInvite } from "@/pages/AcceptInvite";
import { GithubReturn } from "@/pages/GithubReturn";
import { Admin } from "@/pages/dashboard/Admin";
import { Analytics } from "@/pages/dashboard/Analytics";
import { PullDetail } from "@/pages/dashboard/PullDetail";
import { Pulls } from "@/pages/dashboard/Pulls";
import { Repositories } from "@/pages/dashboard/Repositories";
import { Settings } from "@/pages/dashboard/Settings";
import { Shell } from "@/pages/dashboard/Shell";
import { useSession } from "@/session";
import { Loading } from "@/ui";

function redirectToWebsiteLogin() {
  const login = new URL(
    import.meta.env.VITE_WEBSITE_LOGIN_URL || "https://codenerve.ai/sign-in"
  );
  login.searchParams.set("redirect", window.location.href);
  window.location.replace(login.href);
}

/* Nothing signed-out renders on a guess.

   A first visit waits for the session; a returning one opens on the session this browser
   last confirmed while it is re-checked (see session.jsx). Never assuming signed-out is
   what prevents the flash of a signed-out screen -- a flash long enough to click through. */
/* `/github/return` carries what GitHub reported in its query; redirecting it to the wizard
   would drop that, and the page itself sends an unfinished account on to the wizard. */
const OUTSIDE_ONBOARDING = new Set(["/onboarding", "/admin", "/github/return"]);

/* Onboarding is setup, and setup happens once.

   Two answers count, because there are two ways to be finished. The flag is the direct
   one, set when the wizard commits. The membership is the older one, and still has to
   count on its own: an invitee joins an organization somebody else configured without
   ever seeing the wizard, and accounts that finished before the flag was read carry no
   timestamp. Asking for both would send those people back through it. */
function onboardingPending({ profile, organizations }) {
  return !profile?.onboardingCompleted && !organizations.length;
}

function Gate({ children }) {
  const session = useSession();
  const location = useLocation();

  if (session.status === "loading") return <Loading label="Checking your session…" />;
  if (session.status === "out") {
    redirectToWebsiteLogin();
    return <Loading label="Redirecting to sign in…" />;
  }

  const pending = onboardingPending(session);
  /* The platform console is the exception: admitting people is not an organization-scoped
     job, and an operator who has not set up an organization of their own still has to be
     able to do it. */
  if (pending && !OUTSIDE_ONBOARDING.has(location.pathname)) {
    return <Navigate to="/onboarding" replace />;
  }
  // Existing administrators can revisit setup; the wizard reuses their organisation.
  if (!pending && location.pathname === "/onboarding" && session.organization?.role !== "org_admin") {
    return <Navigate to="/" replace />;
  }
  return children;
}

/* An operator seeded from the command line is finished with setup but belongs to no
   organization, so every org-scoped screen would render empty. The admission queue is
   the screen they actually came for.

   Only an administrator who has not finished setup lands on the wizard, which resumes
   where they left off. Sending every administrator there put people who had completed
   it back through it on each sign in. They can still revisit it from its own URL. */
function Home() {
  const { organization, profile } = useSession();
  const destination = !organization
    ? "/admin"
    : organization.role === "org_admin" && !profile?.onboardingCompleted ? "/onboarding" : "/analytics";
  return <Navigate to={destination} replace />;
}

export function App() {
  return (
    <>
      <Routes>
        {/* Redeemed whether or not anyone is signed in: the token is the authority,
            and the invitee may have no account yet. */}
        <Route path="/invite" element={<AcceptInvite />} />
        <Route
          path="/onboarding"
          element={
            <Gate>
              <Onboarding />
            </Gate>
          }
        />
        <Route
          path="/github/return"
          element={
            <Gate>
              <GithubReturn />
            </Gate>
          }
        />
        <Route
          element={
            <Gate>
              <Shell />
            </Gate>
          }
        >
          <Route path="/" element={<Home />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/pulls" element={<Pulls />} />
          <Route path="/pulls/:jobId" element={<PullDetail />} />
          <Route path="/repositories" element={<Repositories />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/:section" element={<Settings />} />
          <Route path="/admin" element={<Admin />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Outside the routes, so an indexing result reaches whichever page is open. */}
      <IndexingNotices />
    </>
  );
}
